import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import { Divider, Grid, InputAdornment, Stack, TextField as InMarKitTextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getTargetingDefaults } from '../../api/ventura/adGroup';
import { scrollTargetStyle } from '../../constants/style';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import { useSnackBar } from '../../hooks/useSnackBar';
import { isBlank, round } from '../../util/helpers';
import mapContractToForm from '../../util/mapContractToForm';
import mapFormToAdGroup from '../../util/mapFormToAdGroup';
import mapLineItemToForm from '../../util/mapLineItemToForm';
import AdType from '../Creatives/forms/fields/AdType';
import Api from '../Creatives/forms/fields/Api';
import Properties from '../Creatives/forms/fields/Properties';
import Size from '../Creatives/forms/fields/Size';
import AccordionSection from '../forms/AccordionSection';
import AutocompleteArray from '../forms/AutocompleteArray';
import Checkbox from '../forms/Checkbox';
import CheckboxArray from '../forms/CheckboxArray';
import FormSection from '../forms/FormSection';
import NumericTextField from '../forms/NumericTextField';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import TextFieldArray from '../forms/TextFieldArray';

const APP_TARGETING_FIELDS = [
  'whitelisted_app_ad_space_ids',
  'blacklisted_app_ad_space_ids',
  'included_app_bundles',
  'excluded_app_bundles',
  'included_app_ad_space_id_lists',
  'excluded_app_ad_space_id_lists',
  'included_app_bundles_lists',
  'excluded_app_bundles_lists',
];

type AdGroupGeneralSectionProps = {
  bidSourceType?: string;
};

const AdGroupGeneralSection = forwardRef((props: AdGroupGeneralSectionProps, ref: Ref<HTMLDivElement>) => {
  const { showSnackBar } = useSnackBar();
  const { apiHost } = useConfig();
  const { setValue, getValues, watch } = useFormContext();
  const contractConfigurationContext = useContractConfigurationContext();

  const lineItemId = getValues('insertionOrderId');
  const maxBid = watch('maxBid');

  const { constants } = useConstants();
  const ALL_AD_TYPES_VALUE_TO_NAME = get(constants, 'CREATIVE.PMP_SUPPORTED_AD_TYPES_VALUE_TO_NAME', {});
  const [acceptedSdkMomentsType, setAcceptedSdkMomentsType] = useState([]);
  const ALL_ACCEPTED_TARGET_TYPES = get(constants, 'CAMPAIGN.ALL_ACCEPTED_TARGET_TYPES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const DEFAULT_TARGETINGS_ENABLED_MOMENTS = get(constants, 'CAMPAIGN.DEFAULT_TARGETINGS_ENABLED_MOMENTS');
  const DEFAULT_TARGETINGS_ENABLED_NONMOMENTS = get(constants, 'CAMPAIGN.DEFAULT_TARGETINGS_ENABLED_NONMOMENTS');

  const ACCEPTED_VIDEO_SDK_MOMENTS_TYPE = get(constants, 'CAMPAIGN.ACCEPTED_VIDEO_SDK_MOMENTS_TYPE', []);
  const ACCEPTED_DISPLAY_SDK_MOMENTS_TYPE = get(constants, 'CAMPAIGN.ACCEPTED_DISPLAY_SDK_MOMENTS_TYPE', []);

  const ACCEPTED_TRACKING_TYPES = get(constants, 'CAMPAIGN.ACCEPTED_TRACKING_TYPES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const mediaType = useWatch({ name: 'mediaType' });

  useEffect(() => {
    if (mediaType) {
      if (mediaType === 'video') {
        setAcceptedSdkMomentsType(
          ACCEPTED_VIDEO_SDK_MOMENTS_TYPE.map((item) => ({
            value: item[0],
            label: item[1],
          }))
        );
      } else {
        setAcceptedSdkMomentsType(
          ACCEPTED_DISPLAY_SDK_MOMENTS_TYPE.map((item) => ({
            value: item[0],
            label: item[1],
          }))
        );
      }
    }
  }, [mediaType, ACCEPTED_VIDEO_SDK_MOMENTS_TYPE, ACCEPTED_DISPLAY_SDK_MOMENTS_TYPE]);

  const targetingDefaultsMutation = useMutation({
    mutationFn: (form: any) => {
      const payload = {
        campaign: mapFormToAdGroup(form),
      };
      return getTargetingDefaults(apiHost, lineItemId, payload);
    },
    onSuccess: (response) => {
      if (!response.errors) {
        const [targetingProfileFromForm, targetingsEnabledFromForm, momentsTypeFromForm] = getValues([
          'targetingProfile',
          'targetingsEnabled',
          'momentsType',
        ]);
        const { targeting_profile: targetingProfileFromResponse, targetings_enabled: targetingsEnabledFromResponse } =
          response;
        const targetingsEnabledFromConstants =
          momentsTypeFromForm === 'NotMoments'
            ? DEFAULT_TARGETINGS_ENABLED_NONMOMENTS
            : DEFAULT_TARGETINGS_ENABLED_MOMENTS;
        setValue('targetingProfile', {
          ...targetingProfileFromForm,
          ...targetingProfileFromResponse,
        });
        // Just like on the Ventura front-end, we will override the form's targetings_enabled
        // with the values from the response of this call and then override again with values from the constants.
        setValue('targetingsEnabled', {
          ...targetingsEnabledFromForm,
          ...targetingsEnabledFromResponse,
          ...targetingsEnabledFromConstants,
        });
      } else {
        showSnackBar(response.errors, 'error', { event: 'targeting_defaults_error', line_item_id: lineItemId });
      }
    },
    onError: () => {
      // this could be an error in a mapper/normalizer function
      showSnackBar('Something wrong happened with getting targeting defaults!', 'error', {
        event: 'targeting_defaults_error',
        line_item_id: lineItemId,
      });
    },
  });

  const onMomentsTypeChange = (e) => {
    // Cleared before reading the form so the targeting-defaults request doesn't echo the old apps back.
    APP_TARGETING_FIELDS.forEach((field) => setValue(`targetingProfile.${field}`, [], { shouldDirty: true }));
    const data = getValues();
    targetingDefaultsMutation.mutate({
      ...data,
      momentsType: e.target.value,
    });
    return e;
  };

  const contractRaw: any = get(contractConfigurationContext, 'contractData', {});
  const contract = mapContractToForm(contractRaw);

  const lineItemsData: any = get(contractConfigurationContext, 'lineItemsData');
  const lineItemRaw = lineItemsData?.find((lineItemItem) => lineItemItem.id === lineItemId);
  const lineItem = mapLineItemToForm(lineItemRaw);
  const pricingRate: number = Number(contract.multiRate ? lineItem?.pricingRate : contract.pricingRate);
  const margin = pricingRate ? round(((pricingRate - maxBid) / pricingRate) * 100) : 0;

  return (
    <FormSection title="General">
      <div ref={ref} style={scrollTargetStyle} />
      <Stack spacing={2}>
        <TextField name="name" label="Ad Group Name" autoComplete="Ad Group Name" autoFocus fullWidth />
        <Grid container>
          <Stack direction="row" width={1} spacing={2}>
            <Grid item xs={4}>
              <Typography>Bidding</Typography>
              <NumericTextField
                fullWidth
                name="maxBid"
                label="Max Bid"
                placeholder="0.90"
                allowNegative={false}
                decimalScale={2}
                sx={{ marginRight: { sm: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography>Margin</Typography>
              <InMarKitTextField
                variant="standard"
                size="small"
                fullWidth
                label="Margin"
                value={`${margin}%`}
                inputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography>URL</Typography>
              <Checkbox name="secure" label="Secure" disabled />
            </Grid>
          </Stack>
        </Grid>
        <TextFieldArray name="campaignUrls" label="Ad Group URL" placeholder="https://example.com" autoComplete="on" />
        <TextFieldArray name="pixelUrls" label="Pixel URL" placeholder="https://example.com" />
        {props.bidSourceType === 'PMP' && (
          <>
            <Typography>PMP</Typography>
            <Grid container columnSpacing={{ xs: 1 }}>
              <Grid item xs={4}>
                <TextField name="pmpData.dealId" label="Deal ID" placeholder="Deal ID" fullWidth />
              </Grid>
              <Grid item xs={4}>
                <NumericTextField
                  name="pmpData.dealBidFloor"
                  label="Deal Bid Floor"
                  placeholder="0.0"
                  allowNegative={false}
                  decimalScale={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 1 }}>
              <AdType items={ALL_AD_TYPES_VALUE_TO_NAME} name="pmpData.creativeType" />
              <Api name="pmpData.creativeApi" />
            </Grid>
            <Grid container columnSpacing={{ xs: 1 }}>
              <Grid item md={6}>
                <Size checkboxes fieldName="creativeSizes" />
              </Grid>
              <Grid item md={6}>
                <Properties name="pmpData.creativeProperties" />
              </Grid>
            </Grid>
          </>
        )}
        <AutocompleteArray
          label="Target Type"
          name="targetType"
          options={ALL_ACCEPTED_TARGET_TYPES}
          normalizeOutput={(outputArray) => outputArray.map((item) => item.value)}
          normalizeInput={(inputArray) => ALL_ACCEPTED_TARGET_TYPES.filter((item) => inputArray?.includes(item.value))}
        />
        <Select
          label="SDK Moments"
          name="momentsType"
          options={acceptedSdkMomentsType}
          normalizeOutput={onMomentsTypeChange}
        />

        <Divider sx={{ alignSelf: 'stretch' }} />
        <AccordionSection>
          <Stack spacing={2}>
            <Stack spacing={1} alignItems="flex-start">
              <Typography>Ad Group Budget</Typography>
              <Stack direction="row" spacing={2}>
                <NumericTextField
                  name="spendPercent"
                  label="Spend Percent"
                  allowNegative={false}
                  decimalScale={0}
                  isAllowed={(input) => isBlank(input.floatValue) || (input.floatValue >= 0 && input.floatValue <= 100)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Reporting
              </Typography>
              <Typography>Tracking Types</Typography>
              <CheckboxArray row name="trackingTypes" options={ACCEPTED_TRACKING_TYPES} />
            </Stack>
          </Stack>
        </AccordionSection>
      </Stack>
    </FormSection>
  );
});

export default AdGroupGeneralSection;
